import type { IHosRuleset } from "hos-tools";
import {Reducer} from "../../lib/redux";
import { extend } from "../../utils/vanilla";
import {DataActions, IAlertAvailabilityPayload, IInspectionPinPayload} from "./actions";
import {
    IDataStore,
    DataActionTypes,
    WorkdayType,
    IIoxActionDictionary,
    ITempAssets,
    IImages,
    ICachedAddress,
    TELDJurisdiction,
    IFeatureFlagStatus,
    IActiveEvents,
    ActiveEventType,
    NativeMotionState
} from "./types";

export let defaultData: IDataStore = {
    boundaryLogDate: {},
    nightMode: false,
    pipMode: false,
    clockDriftMs: 0,
    workday: {},
    tempAssets: {},
    ioxLogActions: {},
    dtcNextRun: {},
    images: {},
    acl: {},
    addressCache: {},
    ruleSets: [],
    ELDJurisdiction: "ELD",
    databaseVersion: "",
    sourceCodeVersion: "",
    clientVersion: "",
    serverFlags: 0,
    debugMode: false,
    lastDatabase: "",
    gpsAccuracyAverage: -1,
    lastUser: "",
    lastServer: "",
    inputHints: {},
    groupSecurityId: [],
    inspectionPin: {},
    alertsAvailability: {},
    featureFlags: {},
    featureFlagOverrides: {},
    originalFeatureFlags: {},
    lastLoginDate: {},
    enableSyncDelay: true,
    instanceId: "",
    driving: undefined,
    handledSdcEndedPcLogIds: {},
    activeEvents: {}
};

const setAcl = (state: IDataStore, payload: IIdDictionary<string[]>): IDataStore => {
    return {
        ...state,
        acl: {
            ...state.acl,
            ...extend(true, {}, payload)
        }
    };
};

const clearAcl = (state: IDataStore, payload: string): IDataStore => {
    let cloneState: IDataStore;
        cloneState = {
            ...state,
            acl: {
                ...state.acl
            }
        };
    delete cloneState.acl[payload];
    return cloneState;
};

// Clear any cached addresses that are older than the provided date.
const clearAddressCache = (state: IDataStore, payload: Date): IDataStore => {
    let payloadString = payload.toISOString();
    return {
        ...state,
        addressCache: Object.keys(state.addressCache).reduce((p: { [coordinates: string]: ICachedAddress }, c) => {
            if (state.addressCache[c].logDate > payloadString) {
                p[c] = state.addressCache[c];
            }
            return p;
        }, {})
    };
};

const setDtcNextRun = (state: IDataStore, payload: IIdDictionary<string>): IDataStore => {
    return {
        ...state,
        dtcNextRun: {
            ...state.dtcNextRun,
            ...extend(true, {}, payload)
        }
    };
};

/**
 * Update/Add a list of items in the address cache.
 * @param state The current state of the data store.
 * @param payload A dictionary keyed on coordinates containing addresses to cache.
 */
const setAddressCache = (state: IDataStore, payload: { [coordinates: string]: ICachedAddress }): IDataStore => {
    return {
        ...state,
        addressCache: {
            ...state.addressCache,
            ...extend(true, {}, payload)
        }
    };
};

/**
 * Update local storage and the redux store with the server version
 * @param state The current state of the data store.
 * @param payload The server version as a string
 */
const setDatabaseVersion = (state: IDataStore, payload: string): IDataStore => {
    return {
        ...state,
        databaseVersion: payload,
        clientVersion: state.sourceCodeVersion || payload
    };
};

/**
 * Update local storage and the redux store with the source code version
 * @param state The current state of the data store.
 * @param payload The source code version as a string
 */
const setSourceCodeVersion = (state: IDataStore, payload: string): IDataStore => {
    return {
        ...state,
        sourceCodeVersion: payload,
        clientVersion: payload || state.clientVersion
    };
};

const setAlertsAvailability = (state: IDataStore, payload: IAlertAvailabilityPayload): IDataStore => {
    const alertsAvailability = payload.alertsAvailability ? extend(true, {}, payload.alertsAvailability) : {};
    return {
        ...state,
        alertsAvailability: {
            ...state.alertsAvailability,
            [payload.userId]: alertsAvailability
        }
    };
};

const clearAlertsAvailability = (state: IDataStore, _): IDataStore => {
    return {
        ...state,
        alertsAvailability: {}
    };
};

const setBoundaryLogDate = (state: IDataStore, payload: IIdDictionary<string>): IDataStore => {
    return {
        ...state,
        boundaryLogDate: {
            ...state.boundaryLogDate,
            ...extend(true, {}, payload)
        }
    };
};

const clearBoundaryLogDate = (state: IDataStore, payload: string): IDataStore => {
    let cloneState: IDataStore;
    cloneState = {
        ...state,
        boundaryLogDate: {
            ...state.boundaryLogDate
        }
    };
    delete cloneState.boundaryLogDate[payload];
    return cloneState;
};

const setNightMode = (state: IDataStore, payload: boolean): IDataStore => {
    return {
        ...state,
        nightMode: payload
    };
};

const setPipMode = (state: IDataStore, payload: boolean): IDataStore => {
    return {
        ...state,
        pipMode: payload
    };
};

const setClockDrift = (state: IDataStore, payload: number): IDataStore => {
    return {
        ...state,
        clockDriftMs: payload
    };
};

const setWorkday = (state: IDataStore, payload: IIdDictionary<WorkdayType>): IDataStore => {
    return {
        ...state,
        workday: {
            ...state.workday,
            ...payload
        }
    };
};

const clearWorkday = (state: IDataStore, payload: string): IDataStore => {
    let cloneState: IDataStore;
    cloneState = {
        ...state,
        workday: {
            ...state.workday
        }
    };
    delete cloneState.workday[payload];
    return cloneState;
};

const setIoxLogAction = (state: IDataStore, payload: IIoxActionDictionary): IDataStore => {
    return {
        ...state,
        ioxLogActions: extend(true, {}, payload)
    };
};

const setIoxHardware = (state: IDataStore, payload: string): IDataStore => {
    return {
        ...state,
        currentHardwareId: payload
    };
};

const setTempAssets = (state: IDataStore, payload: IIdDictionary<ITempAssets>): IDataStore => {
    return {
        ...state,
        tempAssets: {
            ...state.tempAssets,
            ...extend(true, {}, payload)
        }
    };
};

const clearTempAssets = (state: IDataStore, payload: string): IDataStore => {
    let cloneState: IDataStore;
    cloneState = {
        ...state,
        tempAssets: {
            ...state.tempAssets
        }
    };
    delete cloneState.tempAssets[payload];
    return cloneState;
};

const clearDTCNextRun = (state: IDataStore, payload: string): IDataStore => {
    let cloneState: IDataStore;
    cloneState = {
        ...state,
        dtcNextRun: {
            ...state.dtcNextRun
        }
    };
    delete cloneState.dtcNextRun[payload];
    return cloneState;
};

const setImages = (state: IDataStore, payload: IImages): IDataStore => {
    return {
        ...state,
        images: extend(true, {}, payload)
    };
};

const setRuleSetCache = (state: IDataStore, payload: IHosRuleset[]): IDataStore => {
    return {
        ...state,
        ruleSets: extend(true, [], payload)
    };
};

const setELDJurisdiction = (state: IDataStore, payload: TELDJurisdiction): IDataStore => {
    return {
        ...state,
        ELDJurisdiction: payload
    };
};

const setServerFlags = (state: IDataStore, payload: number): IDataStore => {
    return {
        ...state,
        serverFlags: payload
    };
};

const setDebugMode = (state: IDataStore, payload: boolean): IDataStore => {
    return {
        ...state,
        debugMode: payload
    };
};

const setLastDatabase = (state: IDataStore, payload: string): IDataStore => {
    return {
        ...state,
        lastDatabase: payload
    };
};

const clearLastDatabase = (state: IDataStore, _): IDataStore => {
    return {
        ...state,
        lastDatabase: ""
    };
};

const setLastUser = (state: IDataStore, payload: string): IDataStore => {
    return {
        ...state,
        lastUser: payload
    };
};

const  setLastServer = (state: IDataStore, payload: string): IDataStore => {
    return {
        ...state,
        lastServer: payload
    };
};

const clearLastServer = (state: IDataStore, _): IDataStore => {
    return {
        ...state,
        lastServer: ""
    };
};

const setInputHints = (state: IDataStore, payload: IIdDictionary<string[]>): IDataStore => {
    return {
        ...state,
        inputHints: {
            ...state.inputHints,
            ...extend(true, {}, payload)
        }
    };
};

const clearInputHints = (state: IDataStore, payload: string): IDataStore => {
    let cloneState: IDataStore;
    cloneState = {
        ...state,
        inputHints: {
            ...state.inputHints
        }
    };
    delete cloneState.inputHints[payload];
    return cloneState;
};

const setGroupSecurityID = (state: IDataStore, payload: ISecurityGroup[]): IDataStore => {
    return {
        ...state,
        groupSecurityId: payload
    };
};

const clearGroupSecurityID = (state: IDataStore, _): IDataStore => {
    return {
        ...state,
        groupSecurityId: []
    };
};

const setInspectionPin = (state: IDataStore, payload: IInspectionPinPayload): IDataStore => {
    if (payload.userId) {
        return {
            ...state,
            inspectionPin: {
                ...state.inspectionPin,
                [payload.userId]: payload.inspectionPin
            }
        };
    }
    return state;
};

const clearInspectionPin = (state: IDataStore, payload: string): IDataStore => {
    let cloneState: IDataStore;
    if (payload) {
        cloneState = {
            ...state,
            inspectionPin: {
                ...state.inspectionPin
            }
        };
        delete cloneState.inspectionPin[payload];
        return cloneState;
    }

    return {
        ...state,
        inspectionPin: {}
    };
};

const setFeatureFlags = (state: IDataStore, payload: { [ id: string ]: IFeatureFlagStatus }): IDataStore => {
    // If there are active overrides, re-apply them to the new server flags
    const hasOverrides = Object.keys(state.featureFlagOverrides).length > 0;

    if (!hasOverrides) {
        return {
            ...state,
            featureFlags: payload
        };
    }

    // Update originalFeatureFlags with new server values for overridden flags
    // Start fresh to avoid keeping stale values for flags the server has removed
    const updatedOriginals: { [id: string]: IFeatureFlagStatus } = {};
    Object.keys(state.featureFlagOverrides).forEach(flagName => {
        if (payload[flagName]) {
            updatedOriginals[flagName] = payload[flagName];
        }
    });

    // Re-apply overrides to the new server flags
    const mergedFlags = { ...payload };
    Object.keys(state.featureFlagOverrides).forEach(flagName => {
        const serverFlag = payload[flagName];
        mergedFlags[flagName] = {
            ...(serverFlag ?? { name: flagName }),
            isEnabled: state.featureFlagOverrides[flagName]
        };
    });

    return {
        ...state,
        featureFlags: mergedFlags,
        originalFeatureFlags: updatedOriginals
    };
};

const setFeatureFlagOverride = (state: IDataStore, payload: { flagName: string; isEnabled: boolean }): IDataStore => {
    const { flagName, isEnabled } = payload;
    const currentFlag = state.featureFlags[flagName];

    // Save original value if not already saved
    const originalFeatureFlags = state.originalFeatureFlags[flagName]
        ? state.originalFeatureFlags
        : {
            ...state.originalFeatureFlags,
            [flagName]: currentFlag ?? { name: flagName, isEnabled: false }
        };

    return {
        ...state,
        originalFeatureFlags,
        featureFlags: {
            ...state.featureFlags,
            [flagName]: {
                ...(currentFlag ?? { name: flagName }),
                isEnabled
            }
        },
        featureFlagOverrides: {
            ...state.featureFlagOverrides,
            [flagName]: isEnabled
        }
    };
};

const clearFeatureFlagOverride = (state: IDataStore, payload: string): IDataStore => {
    const original = state.originalFeatureFlags[payload];
    const newOverrides = { ...state.featureFlagOverrides };
    const newOriginals = { ...state.originalFeatureFlags };
    const newFeatureFlags = { ...state.featureFlags };

    delete newOverrides[payload];
    delete newOriginals[payload];

    if (original) {
        newFeatureFlags[payload] = original;
    } else {
        // Server removed this flag, so remove it entirely
        delete newFeatureFlags[payload];
    }

    return {
        ...state,
        featureFlags: newFeatureFlags,
        featureFlagOverrides: newOverrides,
        originalFeatureFlags: newOriginals
    };
};

const clearAllFeatureFlagOverrides = (state: IDataStore): IDataStore => {
    const restoredFlags = { ...state.featureFlags };

    Object.keys(state.featureFlagOverrides).forEach(flagName => {
        const original = state.originalFeatureFlags[flagName];
        if (original) {
            restoredFlags[flagName] = original;
        } else {
            // Server removed this flag, so remove it entirely
            delete restoredFlags[flagName];
        }
    });

    return {
        ...state,
        featureFlags: restoredFlags,
        featureFlagOverrides: {},
        originalFeatureFlags: {}
    };
};

const setlastLoginDate = (state: IDataStore, payload: IIdDictionary<string>): IDataStore => {
    return {
        ...state,
        lastLoginDate: {
            ...state.lastLoginDate,
            ...extend(true, {}, payload)
        }
    };
};

const clearLastLoginDate = (state: IDataStore, payload: string): IDataStore => {
    let cloneState: IDataStore;
    cloneState = {
        ...state,
        lastLoginDate: {
            ...state.lastLoginDate
        }
    };
    delete cloneState.lastLoginDate[payload];
    return cloneState;
};

const setGpsAccuracyAverage = (state: IDataStore, payload: number): IDataStore => {
    return {
        ...state,
        gpsAccuracyAverage: payload
    };
};

const setEnableSyncDelay = (state: IDataStore, payload: boolean): IDataStore => {
    return {
        ...state,
        enableSyncDelay: payload
    };
};

const setInstanceId = (state: IDataStore, payload: string): IDataStore => {
    return {
        ...state,
        instanceId: payload
    };
};

const setDriving = (state: IDataStore, payload: boolean): IDataStore => {
    if (state.driving === payload) {
        return state;
    }
    return {
        ...state,
        driving: payload,
        lastVehicleLocation: undefined
    };
};

const NATIVE_MOTION_STATES: readonly NativeMotionState[] = ["moving", "stopped", "unknown"];

// The payload comes from an injected script, so anything but a known state or undefined (the host handing motion
// back to the web app) is ignored: a stray value must not read as "stopped".
const setNativeMotion = (state: IDataStore, payload?: NativeMotionState): IDataStore => {
    if (payload !== undefined && NATIVE_MOTION_STATES.indexOf(payload) === -1) {
        return state;
    }
    if (state.nativeMotion === payload) {
        return state;
    }
    return {
        ...state,
        nativeMotion: payload
    };
};

const setHandledSdcEndedPcLogId = (state: IDataStore, payload: IIdDictionary<string>): IDataStore => {
    return {
        ...state,
        handledSdcEndedPcLogIds: {
            ...state.handledSdcEndedPcLogIds,
            ...payload
        }
    };
};

const addActiveEvent = (state: IDataStore, payload: IActiveEvents): IDataStore => {
    return { ...state, activeEvents: { ...state.activeEvents, ...payload } };
};

const removeActiveEvent = (state: IDataStore, payload: ActiveEventType): IDataStore => {
    const { [payload]: removed, ...rest } = state.activeEvents || {};
    return { ...state, activeEvents: rest };
};

const clearAllActiveEvents = (state: IDataStore): IDataStore => {
    return { ...state, activeEvents: {} };
};

const setVehicleLocation = (state: IDataStore, payload?: ICoordinate): IDataStore => {
    const lastVehicleLocation = payload ? extend(true, {}, payload) : undefined;
    return {
        ...state,
        lastVehicleLocation: lastVehicleLocation
    };
};

const dataActionTypesReducersDictionary: EnumDictionary<DataActionTypes, Function> = {
    [DataActionTypes.SET_BOUNDARY_LOG_DATE]: setBoundaryLogDate,
    [DataActionTypes.CLEAR_BOUNDARY_LOG_DATE]: clearBoundaryLogDate,
    [DataActionTypes.SET_NIGHTMODE]: setNightMode,
    [DataActionTypes.SET_PIPMODE]: setPipMode,
    [DataActionTypes.SET_CLOCK_DRIFT]: setClockDrift,
    [DataActionTypes.SET_WORKDAY]: setWorkday,
    [DataActionTypes.CLEAR_WORKDAY]: clearWorkday,
    [DataActionTypes.SET_IOX_LOG_ACTION]: setIoxLogAction,
    [DataActionTypes.SET_IOX_HARDWAREID]: setIoxHardware,
    [DataActionTypes.SET_TEMP_ASSETS]: setTempAssets,
    [DataActionTypes.CLEAR_TEMP_ASSETS]: clearTempAssets,
    [DataActionTypes.SET_DTCNEXTRUN]: setDtcNextRun,
    [DataActionTypes.CLEAR_DTCNEXTRUN]: clearDTCNextRun,
    [DataActionTypes.SET_IMAGES]: setImages,
    [DataActionTypes.CLEAR_ACL]: clearAcl,
    [DataActionTypes.SET_ACL]: setAcl,
    [DataActionTypes.SET_GPS_ACCURACY_AVERAGE]: setGpsAccuracyAverage,
    [DataActionTypes.CLEAR_ADDRESS_CACHE]: clearAddressCache,
    [DataActionTypes.SET_ADDRESS_CACHE]: setAddressCache,
    [DataActionTypes.SET_RULESETS_CACHE]: setRuleSetCache,
    [DataActionTypes.SET_ELD_JURISDICTION]: setELDJurisdiction,
    [DataActionTypes.SET_DATABASE_VERSION]: setDatabaseVersion,
    [DataActionTypes.SET_SOURCE_CODE_VERSION]: setSourceCodeVersion,
    [DataActionTypes.SET_SERVER_FLAGS]: setServerFlags,
    [DataActionTypes.SET_DEBUGMODE]: setDebugMode,
    [DataActionTypes.CLEAR_LAST_DATABASE]: clearLastDatabase,
    [DataActionTypes.SET_LAST_DATABASE]: setLastDatabase,
    [DataActionTypes.SET_LAST_USER]: setLastUser,
    [DataActionTypes.CLEAR_LAST_SERVER]: clearLastServer,
    [DataActionTypes.SET_LAST_SERVER]: setLastServer,
    [DataActionTypes.CLEAR_INPUT_HINTS]: clearInputHints,
    [DataActionTypes.SET_INPUT_HINTS]: setInputHints,
    [DataActionTypes.SET_GROUP_SECURITY_ID]: setGroupSecurityID,
    [DataActionTypes.CLEAR_GROUP_SECURITY_ID]: clearGroupSecurityID,
    [DataActionTypes.SET_INSPECTION_PIN]: setInspectionPin,
    [DataActionTypes.CLEAR_INSPECTION_PIN]: clearInspectionPin,
    [DataActionTypes.SET_ALERTS_AVAILABILITY]: setAlertsAvailability,
    [DataActionTypes.CLEAR_ALERTS_AVAILABILITY]: clearAlertsAvailability,
    [DataActionTypes.SET_FEATURE_FLAGS]: setFeatureFlags,
    [DataActionTypes.SET_FEATURE_FLAG_OVERRIDE]: setFeatureFlagOverride,
    [DataActionTypes.CLEAR_FEATURE_FLAG_OVERRIDE]: clearFeatureFlagOverride,
    [DataActionTypes.CLEAR_ALL_FEATURE_FLAG_OVERRIDES]: clearAllFeatureFlagOverrides,
    [DataActionTypes.SET_LAST_LOGIN_DATE]: setlastLoginDate,
    [DataActionTypes.CLEAR_LAST_LOGIN_DATE]: clearLastLoginDate,
    [DataActionTypes.SET_ENABLE_SYNC_DELAY]: setEnableSyncDelay,
    [DataActionTypes.SET_INSTANCE_ID]: setInstanceId,
    [DataActionTypes.SET_DRIVING]: setDriving,
    [DataActionTypes.SET_NATIVE_MOTION]: setNativeMotion,
    [DataActionTypes.SET_HANDLED_SDC_ENDED_PC_LOG_ID]: setHandledSdcEndedPcLogId,
    [DataActionTypes.SET_VEHICLE_LOCATION]: setVehicleLocation,
    [DataActionTypes.ADD_ACTIVE_EVENT]: addActiveEvent,
    [DataActionTypes.REMOVE_ACTIVE_EVENT]: removeActiveEvent,
    [DataActionTypes.CLEAR_ALL_ACTIVE_EVENTS]: clearAllActiveEvents
};

export let data: Reducer<IDataStore> = (state = defaultData, action: DataActions): IDataStore => {
    let stateMethod = dataActionTypesReducersDictionary[action.type];
    return stateMethod ? stateMethod(state, action.payload) : state;
};
